import React, { useEffect, useMemo, useState } from "react";
import { FiPackage, FiRefreshCw, FiTruck } from "react-icons/fi";
import sellerApi from "../../utils/sellerApi";
import { showError, showSuccess } from "../../utils/Toast";
import "../../Styles/Seller/Product.css";

const formatStatusLabel = (value) => {
  if (!value) return "-";

  return String(value)
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleString();
};

const getLatestTrackedAt = (shipment) => {
  const directValue =
    shipment?.last_tracked_at ||
    shipment?.tracking_response?.last_tracked_at ||
    null;

  if (directValue) return directValue;

  const events = shipment?.events || shipment?.tracking_response?.events || [];
  const latest = events.reduce((candidate, event) => {
    const eventTime =
      event?.event_time ||
      event?.created_at ||
      event?.timestamp ||
      event?.dateTime ||
      null;

    if (!eventTime) return candidate;
    if (!candidate) return eventTime;
    return new Date(eventTime).getTime() > new Date(candidate).getTime()
      ? eventTime
      : candidate;
  }, null);

  return latest;
};

const buildShipmentStatus = (shipment) =>
  shipment?.tracking_response?.status ||
  shipment?.shipment_status ||
  shipment?.delivery_status ||
  null;

const buildLastStatus = (shipment) =>
  shipment?.last_tracking_status_description ||
  shipment?.tracking_response?.last_status_description ||
  shipment?.tracking_response?.status ||
  shipment?.shipment_status ||
  null;

const extractEvents = (shipment) =>
  Array.isArray(shipment?.events)
    ? shipment.events
    : Array.isArray(shipment?.tracking_response?.events)
      ? shipment.tracking_response.events
      : [];

const extractOrderSummary = (order = {}) => ({
  orderId: order?.order_id || order?.id || null,
  orderNumber:
    order?.order_number ||
    order?.orderNo ||
    order?.order_number_string ||
    (order?.order_id ? `ORD-${order.order_id}` : "Order"),
  customerName:
    order?.username ||
    order?.customer_name ||
    order?.user?.username ||
    order?.user?.name ||
    "Customer",
  productName:
    order?.first_product_name ||
    order?.product_name ||
    order?.product?.name ||
    order?.item?.product_name ||
    "Product",
});

const applyTrackingToShipment = (shipment, tracking) => ({
  ...shipment,
  shipment_status: tracking?.status || shipment?.shipment_status || null,
  tracking_response: tracking || shipment?.tracking_response || null,
  events: tracking?.events || shipment?.events || [],
  last_tracking_status_code:
    tracking?.last_status_code || shipment?.last_tracking_status_code || null,
  last_tracking_status_description:
    tracking?.last_status_description ||
    shipment?.last_tracking_status_description ||
    null,
  last_tracked_at: getLatestTrackedAt({
    ...shipment,
    tracking_response: tracking,
    events: tracking?.events || shipment?.events || [],
  }),
});

export default function ShipmentTracking() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let active = true;

    const loadShipments = async () => {
      try {
        setLoading(true);
        const ordersResponse = await sellerApi.get("seller/orders");
        const orders = Array.isArray(ordersResponse.data?.data)
          ? ordersResponse.data.data
          : [];

        const shipmentRows = await Promise.all(
          orders.map(async (order) => {
            const summary = extractOrderSummary(order);
            if (!summary.orderId) return null;

            try {
              const shipmentResponse = await sellerApi.get(
                `seller/orders/${summary.orderId}/shipment`
              );
              const shipment = shipmentResponse.data?.data || null;
              if (!shipment) return null;

              return {
                ...summary,
                shipment,
              };
            } catch {
              return null;
            }
          })
        );

        if (!active) return;
        setRows(shipmentRows.filter(Boolean));
      } catch (error) {
        if (!active) return;
        showError(
          error.response?.data?.message ||
            "Unable to load seller shipment tracking."
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadShipments();

    return () => {
      active = false;
    };
  }, []);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) =>
      [
        row.orderNumber,
        row.customerName,
        row.productName,
        row.shipment?.awb_number,
        buildShipmentStatus(row.shipment),
        buildLastStatus(row.shipment),
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(query)
      )
    );
  }, [rows, search]);

  const handleTrackShipment = async (row) => {
    const shipmentId = row?.shipment?.id;
    const orderId = row?.orderId;

    if (!shipmentId && !orderId) {
      showError("Shipment is not available for tracking.");
      return;
    }

    try {
      setRefreshingId(shipmentId || orderId);
      const response = shipmentId
        ? await sellerApi.get(`seller/shipments/${shipmentId}/track`)
        : await sellerApi.get(`seller/orders/${orderId}/shipment/track`);
      const tracking = response.data?.data || null;

      setRows((current) =>
        current.map((item) =>
          item.orderId === row.orderId
            ? {
                ...item,
                shipment: applyTrackingToShipment(item.shipment, tracking),
              }
            : item
        )
      );

      showSuccess(
        response.data?.message || "Shipment tracking fetched successfully."
      );
    } catch (error) {
      showError(
        error.response?.data?.message ||
          "Unable to fetch shipment tracking at the moment."
      );
    } finally {
      setRefreshingId(null);
    }
  };

  return (
    <div className="mbp-product-page">
      <div className="mbp-product-header">
        <div>
          <h1>Shipment Tracking</h1>
          <p className="mbp-subtitle">
            Track shipments linked to your seller orders only.
          </p>
        </div>
      </div>

      <div className="mbp-product-card">
        <div className="mbp-product-filters">
          <div className="mbp-search-wrap">
            <FiTruck className="mbp-search-icon" />
            <input
              className="mbp-search-input"
              placeholder="Search by order, product, AWB, customer, or status"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 16 }}>Loading shipment tracking...</div>
        ) : filteredRows.length === 0 ? (
          <div style={{ padding: 16 }}>
            No seller shipments found for tracking.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 16 }}>
            {filteredRows.map((row) => {
              const shipment = row.shipment || {};
              const shipmentStatus = formatStatusLabel(
                buildShipmentStatus(shipment)
              );
              const lastStatus = formatStatusLabel(buildLastStatus(shipment));
              const lastTrackedAt = formatDateTime(getLatestTrackedAt(shipment));
              const events = extractEvents(shipment);
              const isRefreshing =
                refreshingId === (shipment.id || row.orderId);

              return (
                <div
                  key={`${row.orderId}-${shipment.id || shipment.awb_number || "shipment"}`}
                  style={{
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    borderRadius: 18,
                    padding: 18,
                    background: "#fff",
                    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.06)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          fontWeight: 700,
                        }}
                      >
                        <FiPackage />
                        <span>{row.orderNumber}</span>
                      </div>
                      <div style={{ marginTop: 8, color: "#475569" }}>
                        <div>Customer: {row.customerName}</div>
                        <div>Product: {row.productName}</div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="mbp-primary-btn"
                      onClick={() => handleTrackShipment(row)}
                      disabled={isRefreshing}
                    >
                      <FiRefreshCw />
                      <span>{isRefreshing ? "Tracking..." : "Track"}</span>
                    </button>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gap: 12,
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      marginTop: 16,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>AWB</div>
                      <div style={{ fontWeight: 600 }}>
                        {shipment.awb_number || "-"}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        Shipment Status
                      </div>
                      <div style={{ fontWeight: 600 }}>{shipmentStatus}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        EMX Last Status
                      </div>
                      <div style={{ fontWeight: 600 }}>{lastStatus}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        Last Updated
                      </div>
                      <div style={{ fontWeight: 600 }}>{lastTrackedAt}</div>
                    </div>
                  </div>

                  <div style={{ marginTop: 18 }}>
                    <div style={{ fontWeight: 700, marginBottom: 10 }}>
                      Tracking Timeline
                    </div>
                    {events.length ? (
                      <div style={{ display: "grid", gap: 10 }}>
                        {events.map((event, index) => (
                          <div
                            key={`${shipment.id || row.orderId}-event-${index}`}
                            style={{
                              borderTop: "1px solid rgba(15, 23, 42, 0.08)",
                              paddingTop: 10,
                            }}
                          >
                            <div style={{ fontWeight: 600 }}>
                              {formatStatusLabel(
                                event.status_description ||
                                  event.status ||
                                  "shipment_update"
                              )}
                            </div>
                            <div style={{ color: "#475569", marginTop: 4 }}>
                              {event.description || "-"}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: 12,
                                flexWrap: "wrap",
                                marginTop: 6,
                                fontSize: 13,
                                color: "#64748b",
                              }}
                            >
                              <span>
                                {formatDateTime(
                                  event.event_time || event.created_at || null
                                )}
                              </span>
                              <span>{event.location || "Location unavailable"}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: "#64748b" }}>
                        Tracking events will appear here after a successful
                        refresh.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
